import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  IconButton
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel, GridPaginationModel, GridRowParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Plus, Search, RotateCcw } from 'lucide-react'; // Icone Empresa
import { Company, CompanyFilter, CompanyListResponse } from '../../types/company'; // Importa tipos
import { CompanyService } from '../../services/companyService';
import { useTheme } from '@mui/material/styles';
import { ptBRGridLocale, getDataGridStyles } from '../../utils/gridLocalization';
import axios from 'axios'; // Para checar tipo de erro
import SuccessSnackbar from '../../components/common/SuccessSnackbar';

// Mock data
const mockCompanies: Company[] = [
  {
    id: 1,
    cnpj: '11.222.333/0001-44',
    name: 'Empresa Exemplo LTDA',
    fantasy_name: 'Exemplo Soluções',
    active: true,
    tax_regime: 'Simples Nacional',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 2,
    cnpj: '44.555.666/0001-77',
    name: 'Consultoria Individual MEI',
    fantasy_name: undefined, // Assuming no fantasy name for MEI in this mock
    active: true,
    tax_regime: 'MEI',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 3,
    cnpj: '77.888.999/0001-00',
    name: 'Comércio Varejista XYZ',
    fantasy_name: 'Loja XYZ',
    active: false,
    tax_regime: 'Simples Nacional',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// Objeto de tradução
const ptBRLocaleText = {
  MuiTablePagination: {
    labelRowsPerPage: 'Linhas por página:',
  },
};

// Função auxiliar para formatar CNPJ
const formatCNPJ = (cnpj: string | null | undefined): string => {
  if (!cnpj) return '';
  // Remover qualquer coisa que não seja dígito
  const digits = cnpj.replace(/\D/g, '');
  // Aplicar a máscara
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

const CompanyListPage = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  
  // <<< Adicionar estados para Snackbar >>>
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Estados para filtros
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'Todos' | 'Ativo' | 'Inativo'>('Todos');
  
  // Estado para paginação (controlado)
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
  
  // Estado para ordenação (controlado)
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'name', sort: 'asc' },
  ]);

  const navigate = useNavigate();
  const theme = useTheme();
  const companyService = useMemo(() => new CompanyService(), []);

  // Buscar dados quando paginação, ordenação ou filtros mudam
  const [triggerSearch, setTriggerSearch] = useState(0);

  useEffect(() => {
    fetchCompanies();
  }, [paginationModel.page, paginationModel.pageSize, sortModel, triggerSearch]);

  const fetchCompanies = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: CompanyFilter = {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        name: searchText || undefined,
        active: statusFilter !== 'Todos' ? (statusFilter === 'Ativo') : undefined,
      };
      
      if (sortModel.length > 0) {
        filters.sortField = sortModel[0].field;
        filters.sortOrder = sortModel[0].sort ?? 'asc';
      }
      
      const response = await companyService.getAll(filters);
      setCompanies(response.data);
      setRowCount(response.totalCount);
      
    } catch (err: any) {
      setError(err.message || 'Falha ao carregar empresas. Tente novamente.');
      setCompanies([]);
      setRowCount(0);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    navigate('/empresas/new');
  };

  const handleEdit = (id: number) => {
    navigate(`/empresas/edit/${id}`);
  };

  const handleDeleteClick = (company: Company) => {
    setCompanyToDelete(company);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!companyToDelete) return;
    setDeleteError(null);
    try {
      await companyService.delete(companyToDelete.id);
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
      fetchCompanies();
      // <<< Mostrar Snackbar >>>
      setSnackbarMessage('Empresa excluída com sucesso.');
      setSnackbarOpen(true);
    } catch (err: any) {
      console.error("Erro ao excluir empresa:", err);
      // <<< Ajustar tratamento de erro para mostrar no diálogo >>>
      let errorMsg = 'Erro ao excluir empresa.';
      if (axios.isAxiosError(err) && err.response) {
        // Se o backend retornar um erro 409 (Conflito) específico
        if (err.response.status === 409) {
           errorMsg = err.response.data?.message || 'Não é possível excluir: empresa possui contratos associados.';
        } else {
          errorMsg = err.response.data?.message || `Erro ${err.response.status}.`;
        } 
      } else if (err.message) {
        errorMsg = err.message;
      }
      setDeleteError(errorMsg); // <<< Usar o estado de erro do diálogo
      // Não fechar o diálogo: 
      // setDeleteDialogOpen(false);
      // setCompanyToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCompanyToDelete(null);
    setDeleteError(null);
  };

  // Handlers para filtros
  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value as 'Todos' | 'Ativo' | 'Inativo');
  };

  const handleSearch = () => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setTriggerSearch(t => t + 1);
  };

  const handleClear = () => {
    setSearchText('');
    setStatusFilter('Todos');
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setTriggerSearch(t => t + 1);
  };

  // Handler para paginação
  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  // Handler para ordenação
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  };

  // <<< Adicionar handler para Snackbar >>>
  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Razão Social',
      flex: 2, // Mais espaço
      sortable: true,
    },
    {
      field: 'fantasy_name',
      headerName: 'Nome Fantasia',
      flex: 1.5,
      sortable: true,
      valueGetter: (params) => params.value || '-', // Mostrar '-' se vazio
    },
    {
      field: 'cnpj',
      headerName: 'CNPJ',
      flex: 1.5,
      sortable: true,
      valueFormatter: (params) => formatCNPJ(params.value as string), // Aplicar formatação
    },
    {
      field: 'active',
      headerName: 'Status',
      flex: 0.5,
      sortable: true,
      renderCell: (params: GridRenderCellParams<any, Company>) => (
        <Chip 
          label={params.value ? 'Ativo' : 'Inativo'} 
          color={params.value ? 'success' : 'error'}
          size="small"
          sx={{ 
            fontSize: '0.75rem', 
            height: '22px',
            fontWeight: 'bold',
            color: 'white',
            '& .MuiChip-label': {
              padding: '0px 8px'
            }
          }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      flex: 0.8,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any, Company>) => (
        <Box> 
          <Button 
            size="small" 
            onClick={() => handleEdit(params.row.id)} 
            title="Editar"
            sx={{ minWidth: 'auto', mr: 0.5, p: '4px' }} 
          >
            <Edit size={18} />
          </Button>
          <Button 
            size="small" 
            onClick={() => handleDeleteClick(params.row)} 
            title="Excluir"
            color="error"
            sx={{ minWidth: 'auto', p: '4px' }} 
          >
            <Trash2 size={18} />
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Typography variant="h5" component="h1">
          Empresas
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<Plus size={16} />} 
          onClick={handleAddNew}
          size="small"
          sx={{ 
            py: 0.5, 
            px: 2, 
            height: 36, 
            fontWeight: 'bold',
            fontSize: '0.75rem',
            borderRadius: 1
          }}
        >
          NOVA EMPRESA
        </Button>
      </Box>

      <Box sx={{ 
          mb: 1.5, 
          p: 1,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius
      }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth 
              label="Buscar por Razão Social, Fantasia ou CNPJ"
              variant="outlined" 
              value={searchText} 
              onChange={handleSearchTextChange} 
              size="small"
              margin="dense"
              placeholder="Digite para buscar..."
              InputProps={{ 
                style: { fontSize: '0.85rem' },
                sx: { py: 0.3, height: 36 },
                startAdornment: <Search size={16} style={{ marginRight: 8, color: '#666' }} />,
              }}
              InputLabelProps={{ 
                style: { fontSize: '0.85rem' } 
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel htmlFor="status-filter" sx={{ fontSize: '0.85rem' }}>Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                sx={{ fontSize: '0.85rem', height: 40 }}
              >
                <MenuItem value="Todos" sx={{ fontSize: '0.85rem' }}>Todos</MenuItem>
                <MenuItem value="true" sx={{ fontSize: '0.85rem' }}>Ativo</MenuItem>
                <MenuItem value="false" sx={{ fontSize: '0.85rem' }}>Inativo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                size="small"
                startIcon={<Search size={16} />}
                sx={{ 
                  py: 0.5, 
                  px: 2,
                  height: 36,
                  fontWeight: 'bold', 
                  fontSize: '0.75rem',
                  borderRadius: 1
                }}
              >
                BUSCAR
              </Button>
              <Button
                variant="outlined"
                onClick={handleClear}
                size="small"
                startIcon={<RotateCcw size={16} />}
                sx={{ 
                  py: 0.5, 
                  px: 2,
                  height: 36,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  borderRadius: 1
                }}
              >
                LIMPAR
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <CardContent sx={{ p: 0, '& .MuiDataGrid-root': { border: 'none' } }}>
          <DataGrid
            rows={companies}
            rowCount={rowCount}
            columns={columns}
            loading={loading}
            
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[5, 10, 25, 50]}
            
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            
            localeText={ptBRGridLocale}
            sx={getDataGridStyles(theme)}
            density="compact"
            autoHeight
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '1rem', pt: 1.5, pb: 1 }}>Confirmar Exclusão</DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <DialogContentText sx={{ fontSize: '0.9rem' }}>
            Tem certeza que deseja excluir a empresa "{companyToDelete?.name}"?
            {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={handleDeleteCancel} size="small">Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus size="small">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
      {/* <<< Adicionar Snackbar ao JSX >>> */}
      <SuccessSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default CompanyListPage; 